import { BrowserRouter, Route, Routes } from "react-router-dom";
import App from "App"; // adjust the path as needed
import Dashboard from "pages/dashboard/Dashboard";
import NotFound from "utilities/404";
import { ProtectedContent, UseCheckStatusRoute } from "utilities/state/routeHooks";
import HomePage from "website/home";
import LoginContainer from "pages/login/login";
import HomeSignup from "website/homesignup";
import { ProfileForm } from "website/homeprofile";
import { useState, useMemo, useEffect } from "react";
import { supabase } from "supabaseClient";
import { Navigate, useLocation } from "../node_modules/react-router-dom/dist/index";
import HomeTitle from "website/hometitle";
import HomeBlogRoll from "website/homeblogroll";
import { useContext } from "react";
import AppContext from "AppContext";
import BlogPost from "website/homeblog";
import ProfileAdmin from "pages/dashboard/profiles/profileadmin";
import DashboardWall from "pages/dashboard/dashboardwall";
import { UserProvider } from "contexts/UserContext";
import { useUserState } from "hooks/useUserState";

// Protected route wrapper component
const ProtectedRoute = ({ children }) => {
  const { isLoading, userData, permissions } = useUserState();
  const [sessionChecked, setSessionChecked] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setHasSession(!!session);
      setSessionChecked(true);
    };
    checkSession();
  }, []);

  // Show nothing while checking authentication
  if (!sessionChecked || isLoading) {
    return null; // Or return a loading spinner component
  }

  // Redirect if no session or no user data
  if (!hasSession || !userData) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Redirect if not registered
  if (!permissions?.isRegistered) {
    return <Navigate to="/" replace />;
  }

  // Redirect to profile if user has session but is not active
  if (hasSession && !permissions?.isActive) {
    return <Navigate to="/profile" replace state={{ from: location }} />;
  }

  return children;
};

// Admin route wrapper component
const AdminRoute = ({ children }) => {
  const { organizations } = useUserState();

  if (!organizations?.admin?.length) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Active route wrapper component for dashboard access
const ActiveRoute = ({ children }) => {
  const { permissions } = useUserState();
  const location = window.location.pathname;

  // If user is active and trying to access / or /profile, redirect to dashboard
  if (permissions?.isActive && (location === '/' || location === '/profile')) {
    return <Navigate to="/dashboard" replace />;
  }

  // If user is not active and trying to access dashboard, redirect to profile
  if (!permissions?.isActive && location.startsWith('/dashboard')) {
    return <Navigate to="/profile" replace />;
  }

  return children;
};

// Add this new component before the Roots function
const UserStateLogger = () => {
  const userState = useUserState();

  useEffect(() => {
    // Only log when loading is complete and we have data
    if (!userState.isLoading && userState) {
      console.warn('User State:', userState);
    }
  }, [
    userState.isLoading,
    userState.userData,
    userState.permissions,
    userState.organizations
  ]);

  return null;
};

function Roots() {
  const [sessionExists, setSessionExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const hasProfile = useContext(AppContext);

  useEffect(() => {
    setLoading(true);

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSessionExists(!!session);
        setLoading(false);
        setAuthChecked(true);
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  // Don't render routes until auth is checked
  if (!authChecked) {
    return null; // or a loading spinner component
  }

  return (
    <BrowserRouter>
      <UserProvider>
        <UserStateLogger />
        <App>
          <Routes>
            <Route
              path="/"
              element={
                <ActiveRoute>
                  <HomePage
                    leftTitle={<HomeTitle />}
                    pageName="home"
                    pageForm={sessionExists ? <ProfileForm /> : <HomeSignup />}
                  />
                </ActiveRoute>
              }
            />
            <Route
              path="/organizations"
              element={
                <HomePage
                  leftTitle={<HomeTitle />}
                  pageName="organizations"
                  pageForm={sessionExists ? <ProfileForm /> : <HomeSignup />}
                />
              }
            />
            <Route
              path="/community"
              element={
                <HomePage
                  pageName="community"
                  leftTitle={<HomeTitle />}
                  pageForm={sessionExists ? <ProfileForm /> : <HomeSignup />}
                />
              }
            />
            <Route
              path="/founders"
              element={
                <HomePage
                  pageName="startups"
                  leftTitle={<HomeTitle />}
                  pageForm={sessionExists ? <ProfileForm /> : <HomeSignup />}
                />
              }
            />
            <Route
              path="/ecosystems"
              element={
                <HomePage
                  pageName="ecosystems"
                  leftTitle={<HomeTitle />}
                  pageForm={sessionExists ? <ProfileForm /> : <HomeSignup />}
                />
              }
            />
            <Route
              path="/service-providers"
              element={
                <HomePage
                  pageName="service providers"
                  leftTitle={<HomeTitle />}
                  pageForm={sessionExists ? <ProfileForm /> : <HomeSignup />}
                />
              }
            />
            <Route
              path="/profile"
              element={
                loading ? null : sessionExists ? (
                  <ActiveRoute>
                    <HomePage
                      pageName="profile"
                      leftTitle={<HomeTitle />}
                      pageForm={<ProfileForm />}
                    />
                  </ActiveRoute>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* Blog */}

            <Route
              path="/canadianwomensfoundation"
              element={<Navigate to="/org/canadianwomensfoundation" replace />}
            />
            <Route
              path="/womenfounders"
              element={<Navigate to="/org/canadianwomensfoundation" replace />}
            />
            <Route
              path="/org/canadianwomensfoundation"
              element={
                <HomePage
                  pageName="canadianwomensfoundation"
                  leftTitle={<HomeTitle />}
                  pageForm={sessionExists ? <ProfileForm /> : <HomeSignup />}
                />
              }
            />
            <Route
              path="/thrivelab"
              element={<Navigate to="/org/thrivelab" replace />}
            />
            <Route
              path="/bdc"
              element={<Navigate to="/org/thrivelab" replace />}
            />
            <Route
              path="/org/thrivelab"
              element={
                <HomePage
                  pageName="thrivelab"
                  leftTitle={<HomeTitle />}
                  pageForm={sessionExists ? <ProfileForm /> : <HomeSignup />}
                />
              }
            />
            <Route
              path="/parkdale"
              element={<Navigate to="/org/parkdale" replace />}
            />
            <Route
              path="/parkdaleinnovates"
              element={<Navigate to="/org/parkdale" replace />}
            />
            <Route
              path="/org/parkdale"
              element={
                <HomePage
                  pageName="parkdaleinnovates"
                  leftTitle={<HomeTitle />}
                  pageForm={sessionExists ? <ProfileForm /> : <HomeSignup />}
                />
              }
            />

            <Route
              path="/news/:slug"
              element={
                <HomePage
                  pageName="news"
                  leftTitle={<HomeBlogRoll />}
                  pageForm={<BlogPost />}
                />
              }
            />

            <Route
              path="/news"
              element={
                <HomePage
                  leftTitle={<HomeBlogRoll />}
                  pageName="news"
                  pageForm={<BlogPost />}
                />
              }
            />
            <Route
              path="/terms"
              element={
                <HomePage
                  leftTitle={<HomeBlogRoll />}
                  pageName="terms"
                  pageForm={<BlogPost />}
                />
              }
            />
            <Route
              path="/privacy"
              element={
                <HomePage
                  leftTitle={<HomeBlogRoll />}
                  pageName="privacy"
                  pageForm={<BlogPost />}
                />
              }
            />
            <Route
              path="/guidelines"
              element={
                <HomePage
                  leftTitle={<HomeBlogRoll />}
                  pageName="guidelines"
                  pageForm={<BlogPost />}
                />
              }
            />
            <Route
              path="/indigenous-relations"
              element={
                <HomePage
                  leftTitle={<HomeBlogRoll />}
                  pageName="news"
                  pageForm={<BlogPost />}
                />
              }
            />
            <Route
              path="/investor-relations"
              element={
                <HomePage
                  leftTitle={<HomeBlogRoll />}
                  pageName="investor-relations"
                  pageForm={<BlogPost />}
                />
              }
            />
            <Route
              path="/copyright"
              element={
                <HomePage
                  leftTitle={<HomeBlogRoll />}
                  pageName="copyright"
                  pageForm={<BlogPost />}
                />
              }
            />
            <Route path="login" element={<LoginContainer />} />

            {/* Protected dashboard routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <ActiveRoute>
                    <Dashboard activeTab="default" tabContent={<DashboardWall />} />
                  </ActiveRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/profile"
              element={
                <ProtectedRoute>
                  <Dashboard activeTab="profile" tabContent={<ProfileForm />} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/admin"
              element={
                <ProtectedRoute>
                  <AdminRoute>
                    <Dashboard activeTab="admin" tabContent={<ProfileAdmin />} />
                  </AdminRoute>
                </ProtectedRoute>
              }
            />

            <Route
              path="*"
              element={
                loading ? null : (
                  <ProtectedContent>
                    <NotFound />
                  </ProtectedContent>
                )
              }
            />
          </Routes>
        </App>
      </UserProvider>
    </BrowserRouter>
  );
}

export default Roots;
